import { extendZodWithOpenApi } from "@asteasolutions/zod-to-openapi";
import { mongoose } from "@typegoose/typegoose";
import { z } from "zod";
import { DeviceTypeConfigurations } from "../../Configurations/DeviceTypeConfiguration/DeviceTypeConfigurations.js";
import { SensorConfigurationsNoExplicitKeys } from "../../Configurations/SensorConfigurations/SensorConfiguration.js";
import type { UnitCategory } from "../../Configurations/SensorConfigurations/SensorConfigurationTypes.js";
import { sensorClusterConfigs } from "../../Configurations/SensorConfigurations/sensorClusters/sensorClusterConfigs.js";
import { sensorElementConfigs } from "../../Configurations/SensorConfigurations/sensorElements/sensorElementConfigs.js";
import { UnitCategoryConfigurations } from "../../Configurations/Units/UnitCategory/UnitCategoryConfigurations.js";
import { standardWarningDescriptions } from "../../Configurations/WarningConfigurations.js";
import { allParsedDataTypes, allRawDataTypes } from "../../HistoricalData.js";
import { AlertSetting } from "../../shared.js";
import { rrulestr } from "../../utils/RRuleLibrary.js";
import { isDeviceVersionSelectorValid } from "../../utils/VerdiDeviceVersionUtilities.js";
import { WarningLevel } from "../../WarningDescription.js";

extendZodWithOpenApi(z);

const dataTypeSpecifierValues = [...allParsedDataTypes, ...allRawDataTypes];
if (dataTypeSpecifierValues.length === 0) {
    throw new Error("No data type keys available for DataTypeSpecifierString");
}

/**
 * Zod schema aligned with `DataTypeSpecifier` in `HistoricalData.ts`:
 * parsed historical types plus `RAW_${sensorType}` entries from sensor configuration keys.
 */
export const DataTypeSpecifierString = z.enum(dataTypeSpecifierValues).openapi({
    type: "string",
    description: "Data type specifier (parsed or RAW_ sensor stream)",
    example: allParsedDataTypes[0],
});

const sproutCheckTypeValues = ["valveState", "sensor", "online", "transitionOnTime", "solenoid"] as const;

/**
 * Zod schema for sprout verification check categories (see device sprout verification checks).
 */
export const SproutCheckTypeString = z.enum(sproutCheckTypeValues).openapi({
    type: "string",
    description: "Sprout verification check type (valve state, sensor, connectivity, transition timing, solenoid)",
    example: "solenoid",
});

/** Wire values accepted by {@link SproutCheckTypeString}. */
export type SproutCheckType = z.infer<typeof SproutCheckTypeString>;

const verificationStatusValues = ["successful", "pending", "inconclusive", "warning", "error", "catastrophic"] as const;

/**
 * Zod schema for per-check sprout verification outcome status (severity ordering in app code uses numeric codes).
 */
export const VerificationStatusString = z.enum(verificationStatusValues).openapi({
    type: "string",
    description: "Sprout verification check outcome (successful through catastrophic)",
    example: "successful",
});

/** Wire values accepted by {@link VerificationStatusString}. */
export type VerificationStatus = z.infer<typeof VerificationStatusString>;

/**
 * Transforms a string into a MongoDB ObjectId in a way that can be used by OpenAPI
 */
export const ObjectIdString = z
    .string()
    .refine((val) => mongoose.Types.ObjectId.isValid(val), {
        message: "Invalid ObjectId format",
    })
    .transform((value) => new mongoose.Types.ObjectId(value))
    .openapi({
        type: "string",
        description: "MongoDB ObjectId (24-character hex string)",
        example: "507f1f77bcf86cd799439011",
    });

/**
 * Zod schema for node_id values: literal prefix `eui-` plus 16 lowercase hex digits (21 characters).
 */
export const NodeIdString = z
    .string()
    .regex(/^eui-[a-f0-9]{16}$/, {
        message: "Must be eui- followed by 16 lowercase hexadecimal digits",
    })
    .openapi({
        type: "string",
        pattern: "^eui-[a-f0-9]{16}$",
        description: "node id (eui- + 16 hex digits)",
        example: "eui-0004a30b001cd4e5",
    });

/** Values accepted by {@link NodeIdString} (use for `node_id` fields on schemas). */
export type NodeId = z.infer<typeof NodeIdString>;

/**
 * Zod schema for phone numbers in E.164 wire format: a leading `+`, then up to 15 digits.
 */
export const E164PhoneNumber = z
    .string()
    .regex(/^\+[1-9]\d{1,14}$/, "must be an E.164 phone number, e.g. +14155550100")
    .openapi({
        type: "string",
        description: "Phone number in E.164 format",
        example: "+14155550100",
    });

const sensorTypeKeys = Object.keys(SensorConfigurationsNoExplicitKeys);
if (sensorTypeKeys.length === 0) {
    throw new Error("SensorConfigurationsNoExplicitKeys produced no keys for SensorTypeString");
}

/**
 * Zod schema that only accepts keys present in {@link SensorConfigurationsNoExplicitKeys}
 * (the same set as the `SensorType` TypeScript type).
 */
export const SensorTypeString = z.enum(sensorTypeKeys).openapi({
    type: "string",
    description: "Registered sensor type key (see SensorConfigurations)",
    example: sensorTypeKeys[0],
});

const deviceTypeKeys = Object.values(DeviceTypeConfigurations)
    .filter((config) => config.isVerdiDevice)
    .map((config) => config.type);
if (deviceTypeKeys.length === 0) {
    throw new Error("DeviceTypeConfigurations produced no first-party keys for DeviceTypeString");
}

/**
 * Zod schema that only accepts the first-party keys of {@link DeviceTypeConfigurations}
 * (the same set as the `FirstPartyDeviceType` TypeScript type).
 */
export const DeviceTypeString = z.enum(deviceTypeKeys).openapi({
    type: "string",
    description: "Registered device type key (see DeviceTypeConfigurations)",
    example: deviceTypeKeys[0],
});

const thirdPartyDeviceTypeKeys = Object.values(DeviceTypeConfigurations)
    .filter((config) => !config.isVerdiDevice)
    .map((config) => config.type);
if (thirdPartyDeviceTypeKeys.length === 0) {
    throw new Error("DeviceTypeConfigurations produced no third-party keys for ThirdPartyDeviceTypeString");
}

/**
 * Zod schema that only accepts the third-party keys of {@link DeviceTypeConfigurations}
 * (the same set as the `ThirdPartyDeviceType` TypeScript type).
 */
export const ThirdPartyDeviceTypeString = z.enum(thirdPartyDeviceTypeKeys).openapi({
    type: "string",
    description: "Registered third-party device type key (see DeviceTypeConfigurations)",
    example: thirdPartyDeviceTypeKeys[0],
});

const warningTypeKeys = Object.keys(standardWarningDescriptions);
if (warningTypeKeys.length === 0) {
    throw new Error("standardWarningDescriptions produced no keys for WarningTypeString");
}

/**
 * Zod schema aligned with keys of {@link standardWarningDescriptions}
 * (the same set as the `WarningType` TypeScript union).
 */
export const WarningTypeString = z.enum(warningTypeKeys as [string, ...string[]]).openapi({
    type: "string",
    description: "Warning type key (see WarningConfigurations.standardWarningDescriptions)",
    example: warningTypeKeys[0],
});

/**
 * Zod schema for {@link WarningLevel} numeric enum values (INFO, WARNING, ERROR).
 * Uses literals instead of `z.enum()` because Zod v4 only supports string enums.
 */
export const WarningLevelString = z
    .union([z.literal(WarningLevel.INFO), z.literal(WarningLevel.WARNING), z.literal(WarningLevel.ERROR)])
    .openapi({
        type: "number",
        description: "Warning severity level (INFO=0, WARNING=1, ERROR=2)",
        example: WarningLevel.WARNING,
    });

/**
 * Zod schema for device product line.
 */
export const ProductLineString = z.enum(["block", "micro", "mega", "mini"]).openapi({
    type: "string",
    description: "Device product line",
    example: "block",
});

/**
 * Zod schema for device hardware variant.
 */
export const HardwareVariantString = z
    .enum([
        "a",
        "b",
        "c",
        "d",
        "e",
        "f",
        "g",
        "h",
        "i",
        "j",
        "k",
        "l",
        "m",
        "n",
        "o",
        "p",
        "q",
        "r",
        "s",
        "t",
        "u",
        "v",
        "w",
        "x",
        "y",
        "z",
    ])
    .openapi({
        type: "string",
        description: "Hardware variant letter",
        example: "a",
    });

/**
 * Zod schema for Verdi device version selector strings.
 */
export const DeviceVersionSelectorString = z
    .string()
    .min(1)
    .refine((value) => isDeviceVersionSelectorValid(value), {
        message: "Invalid device version selector. Consult notion for device version selector syntax",
    })
    .openapi({
        type: "string",
        description: "Verdi device version selector string",
        example: "4.block.*.*.*",
    });

/**
 * Zod schema for {@link AlertSetting} numeric enum values.
 */
export const AlertSettingSchema = z
    .union([
        z.literal(AlertSetting.Ignore),
        z.literal(AlertSetting.AddToQueue),
        z.literal(AlertSetting.SMSEveryHour),
        z.literal(AlertSetting.SMSImmediate),
        z.literal(AlertSetting.SMSImmediateEmergency),
    ])
    .openapi({
        example: AlertSetting.AddToQueue,
        description: "Alert delivery setting",
        type: "number",
    });

// Fractional milliseconds are allowed on purpose. Existing irrigation durations were saved from an hours slider
// (for example 1.33 hours * hourMs), so plenty of stored values are not whole milliseconds. Requiring an integer here
// makes v2 responses fail response validation for those records instead of just returning the stored value.
export const milliseconds = z.number().min(0).openapi({
    type: "number",
    description: "Milliseconds",
    example: 1000,
});

const deviceOverrideSettingValues = ["open", "closed", "none"] as const;

/**
 * Zod schema for manual valve override settings (remote or device-initiated).
 */
export const DeviceOverrideSettingString = z.enum(deviceOverrideSettingValues).openapi({
    type: "string",
    description: "Manual override valve setting (open, closed, or none / schedule)",
    example: "none",
});

/** Wire values accepted by {@link DeviceOverrideSettingString}. */
export type DeviceOverrideSetting = z.infer<typeof DeviceOverrideSettingString>;

const sensorClusterConfigKeys = Object.keys(sensorClusterConfigs) as [string, ...string[]];
if (sensorClusterConfigKeys.length === 0) {
    throw new Error("sensorClusterConfigs produced no keys for SensorClusterConfigKeyString");
}

/**
 * Zod schema that only accepts keys present in {@link sensorClusterConfigs}
 * (the same set as the `SensorClusterConfigKey` TypeScript type).
 */
export const SensorClusterConfigKeyString = z.enum(sensorClusterConfigKeys).openapi({
    type: "string",
    description: "Registered sensor cluster config key (see sensorClusterConfigs)",
    example: sensorClusterConfigKeys[0],
});

const sensorElementConfigKeys = Object.keys(sensorElementConfigs) as [string, ...string[]];
if (sensorElementConfigKeys.length === 0) {
    throw new Error("sensorElementConfigs produced no keys for SensorElementConfigKeyString");
}

/**
 * Zod schema that only accepts keys present in {@link sensorElementConfigs}
 * (the same set as the `SensorElementConfigKey` TypeScript type).
 */
export const SensorElementConfigKeyString = z.enum(sensorElementConfigKeys).openapi({
    type: "string",
    description: "Registered sensor element config key (see sensorElementConfigs)",
    example: sensorElementConfigKeys[0],
});

// Keys of UnitCategoryConfigurations are the runtime source of truth for the UnitCategory
const unitCategoryKeys = Object.keys(UnitCategoryConfigurations) as [UnitCategory, ...UnitCategory[]];
if (unitCategoryKeys.length === 0) {
    throw new Error("UnitCategoryConfigurations produced no keys for UnitCategoryString");
}

/**
 * Zod schema that only accepts keys present in {@link UnitCategoryConfigurations}
 * (the same set as the `UnitCategory` TypeScript type)
 */
export const UnitCategoryString = z.enum(unitCategoryKeys).openapi({
    type: "string",
    description: "Unit category (see verdi-common UnitCategoryConfigurations)",
    example: unitCategoryKeys[0],
});

/**
 * Zod schema for numeric ranges that are either empty or contain exactly two numbers.
 */
export const valueRangeArray = z
    .array(z.number())
    .refine((acceptableValueRange) => acceptableValueRange.length === 0 || acceptableValueRange.length === 2, {
        message: "Value range must be empty or contain exactly 2 numbers",
    });

/**
 * Zod schema for recurrence rule strings accepted by the shared RRule parser.
 */
export const RRuleString = z
    .string()
    .refine(
        (value) => {
            try {
                rrulestr(value);
                return true;
            } catch {
                return false;
            }
        },
        {
            message: "Invalid rrule string",
        },
    )
    .openapi({
        type: "string",
        description: "Recurrence rule string in rrule format",
        example: "DTSTART:20210609T000506Z\nRRULE:FREQ=WEEKLY;BYDAY=MO,TU,WE,TH,FR,SA,SU;INTERVAL=1",
    });

/** Descope custom attribute value types (string, boolean, number, or string array). */
export const DescopeAttributesTypesSchema = z.union([z.string(), z.boolean(), z.number(), z.array(z.string())]);
export type DescopeAttributesTypes = z.infer<typeof DescopeAttributesTypesSchema>;

/** Descope user lifecycle status values. */
export const DescopeUserStatusSchema = z.enum(["enabled", "disabled", "invited"]);
export type DescopeUserStatus = z.infer<typeof DescopeUserStatusSchema>;
